import React, { useState } from "react";
import { Pencil, Check, X } from "lucide-react";
import api, { apiErr } from "../lib/api";
import { toast } from "sonner";
import { fmtDate } from "../lib/format";

/**
 * SPT-PTE-2026-0061 — lightweight inline pen-edit for a job item's "PO
 * Received" date. Auto-stamped by the backend the first time a PO number
 * is captured, but staff can correct it here if the auto-stamped date is
 * wrong (e.g. backfilled from an approximate audit-trail date, or the PO
 * was physically received earlier than it was typed into the system).
 */
export function InlinePoDateEdit({ item, onSaved, testidPrefix = "inline-po-date" }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(item.po_date || "");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/job-items/${item.id}/payment`, { po_date: value || null });
      toast.success("PO received date updated");
      setEditing(false);
      onSaved && onSaved();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  if (!editing) {
    return (
      <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
        <span className="mono" style={{ fontSize: 11.5 }} data-testid={`${testidPrefix}-value-${item.id}`}>
          {item.po_date ? fmtDate(item.po_date) : <span className="t-mut">—</span>}
        </span>
        <button
          onClick={(e) => { e.stopPropagation(); setValue(item.po_date || ""); setEditing(true); }}
          title="Edit PO received date"
          data-testid={`${testidPrefix}-edit-${item.id}`}
          style={{ background: "none", border: "none", cursor: "pointer", padding: 2, color: "var(--text-3)", display: "inline-flex" }}>
          <Pencil size={11} />
        </button>
      </span>
    );
  }

  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }} onClick={(e) => e.stopPropagation()}>
      <input
        type="date"
        className="input mono"
        style={{ width: 120, fontSize: 11.5, padding: "2px 6px" }}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        autoFocus
        disabled={busy}
        data-testid={`${testidPrefix}-input-${item.id}`}
        onKeyDown={(e) => {
          if (e.key === "Enter") save();
          if (e.key === "Escape") setEditing(false);
        }}
      />
      <button onClick={save} disabled={busy} title="Save"
        data-testid={`${testidPrefix}-save-${item.id}`}
        style={{ background: "none", border: "none", cursor: "pointer", color: "var(--gold-deep)", display: "inline-flex" }}>
        <Check size={13} />
      </button>
      <button onClick={() => setEditing(false)} disabled={busy} title="Cancel"
        data-testid={`${testidPrefix}-cancel-${item.id}`}
        style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-3)", display: "inline-flex" }}>
        <X size={13} />
      </button>
    </span>
  );
}
